import config from 'react-global-configuration';
import type { LatLngExpression } from 'leaflet';
import type { AppOptions, Constraints, SsoService } from '../types';

export let API_URL = process.env.REACT_APP_API_URL || '/api';

// Default app options
export let APP_OPTIONS = {
  env: 'production',
  multilingual: 'false',
  defaultLanguage: 'nl',
  siteName: 'VeiligheidNL',
  apiUrl: API_URL,
  apolloSearchUrl: '/apollo/api/graphql',
  searchkitHostUrl: '/api-proxy/elasticsearch-proxy?_api_proxy_uri=',
  searchkitIndexAllContent: 'elasticsearch_index_drupal_content',
  searchkitIndexPromotedContentThemes:
    'elasticsearch_index_drupal_kennisaanbod_themas',
  searchkitResultAttributes: ['url', 'combined_types', 'type', 'title'],
  searchkitAutocompleteAttributes: ['autocomplete_field'],
  searchkitAutocompleteFuzziness: 0,
  searchkitAutocompleteHits: 3,
  searchkitAutocompleteDebounceTime: 300,
  searchkitAutosuggestAttributes: ['title'],
  searchkitAutosuggestHits: 5,
  searchkitAutosuggestDebounceTime: 300,
  searchkitAutocompleteRenderSuggestionsThreshold: 2,
  searchPath: '/zoeken',
  contentFetchCredentials: 'include',
  searchkitIndexProfessionals: 'elasticsearch_index_drupal_professionals',
  mapBoxApiKey:
    'pk.eyJ1IjoidmVpbGlnaGVpZG5sIiwiYSI6ImNsaGhpbG9xdTFjazYzcW80YnpyYTBiMWIifQ.ezXf20P8AwYkeivOLOu_yw',
  mapBoxStyle: 'mapbox://styles/veiligheidnl/clhq71m5s01u401quej6z5wre',
  recapchaKey: '',
  sentryDSN: process.env.REACT_APP_SENTRY_DSN || '',
  sentryEnabled: process.env.REACT_APP_SENTRY_ENABLED || 'false',
  themeMenuFeaturedBlock: process.env.REACT_APP_THEME_MENU_FEATURED_BLOCK || '',
  riskMonitorCalculatorBlockId: 3,
  ssoServices: [
    {
      id: 'risk_monitor',
      label: 'Risicomonitor',
      url: 'https://risico-monitor.nl/login',
      icon: 'icon-risk-monitor-logo-circle',
      roles: ['risico_monitor_user', 'risico_monitor_maintainer'],
      body: 'De Risicomonitor helpt organisaties in Kinderopvang, Jeugdzorg en Sociaal Werk om een gezonde en veilige omgeving te creëren voor medewerkers én kinderen.',
      clientIds: [] as string[],
    },
    {
      id: 'valanalyse',
      label: 'Valanalyse',
      url: 'https://example.com/my-tool',
      icon: 'icon-risk-monitor-logo-circle',
      roles: ['valanalyse_user'],
      body: 'Mijn omschrijving',
      clientIds: ['123'],
      constraints: {
        field_job_title: {
          disallow: ['Paramedicus', 'Zorgprofessional'],
          allow: [
            'Paramedicus - fysiotherapeut',
            'Paramedicus - Oefentherapeut',
            'Paramedicus - Ergotherapeut',
            'Paramedicus - Podotherapeut',
            'Paramedicus - Diëtist',
            'Paramedicus - Overig',
            'Zorgprofessional - Apotheker',
            'Zorgprofessional - Wijkverpleegkundige',
            'Zorgprofessional - Verpleegkundig specialist',
            'Zorgprofessional - Speclalist ouderengeneeskunde',
            'Zorgprofessional - Overig',
          ],
          required: true,
        },
      } as Constraints,
    },
  ],
  minimumPasswordCharacterCount: 8,
  accountPagePersonalTab: 'personal',
  accountPageOverviewTab: 'overview',
  accountPageEmailTab: 'email',
  accountPageAddressesTab: 'addresses',
  accountPageServicesTab: 'services',
  accountPageOrdersTab: 'orders',
  accountPageProductsTab: 'products',
  // For local dev trough a proxy:
  // env: 'development',
  // apiUrl: 'http://localhost:3000/api',
  // apolloSearchUrl: 'http://localhost:3000/apollo/api/graphql',
  // searchkitHostUrl: 'http://localhost:3000/search',
};

export const APP_ENV = process.env.REACT_APP_ENV;
export const __DEBUG__ = APP_ENV !== 'production';

export const REACT_APP_VERSION = process.env.REACT_APP_VERSION || 'unknown';

export let USE_SENTRY = APP_OPTIONS.sentryEnabled;
export let SENTRY_DSN = APP_OPTIONS.sentryDSN;

export let MULTILINGUAL = APP_OPTIONS.multilingual.toLowerCase() === 'true';
export let DEFAULT_LANGUAGE = APP_OPTIONS.defaultLanguage;
// Default ISO country for addresses (the webshop serves the Netherlands only).
export const DEFAULT_COUNTRY_CODE = 'NL';
export let SITE_NAME = APP_OPTIONS.siteName;
export let THEME_MENU_FEATURED_BLOCK = APP_OPTIONS.themeMenuFeaturedBlock;
export const SSO_RISK_MONITOR_REQUEST_WEBFORM_ID = 'risico_monitor_aanmelding';
export let SSO_SERVICES: SsoService[] = APP_OPTIONS.ssoServices;
export let MINIMUM_PASSWORD_CHARACTER_COUNT: number =
  APP_OPTIONS.minimumPasswordCharacterCount;

export const APP_ROOT_ELEMENT_ID = 'root';

export const GTM_SOURCE_MIJN_VNL = 'mijn_vnl';
export const GTM_SOURCE_LOGIN = 'login';
export const GTM_SOURCE_REGULAR = 'regular';
export const GTM_SOURCE_WEBSHOP = 'webshop';
export const GTM_SOURCE_LOGIN_WALL = 'login_wall';
export const GTM_SOURCE_RISK_MONITOR = 'risk_monitor';
export const GTM_SOURCE_RISK_NORMAL = 'normal';
export const GTM_SOURCE_BANNER = 'banner';
export const GTM_SOURCE_NEWSLETTER_PAGE = 'newsletter_page';

// The value is where the breakpoint starts (mobile-first).
// These values are taken from '/src/src/styles/assets/_variables.scss'.
export const SCREEN_MOBILE = 0;
export const SCREEN_TABLET = 768;
export const SCREEN_NORMAL = 992;
export const SCREEN_WIDE = 1240;
export const SCREEN_HUGE = 2000;

export const IS_SERVER = !(
  typeof window !== 'undefined' &&
  window.document &&
  window.document.createElement
);
export const IS_CLIENT = !IS_SERVER;

// Domains.
export const DOMAIN_VNL = 'vnl';
export const DOMAIN_DRUPAL_IDENTIFIER_VNL = 'veiligheid_nl';
export const DOMAIN_KVNL = 'kvnl';
export const DOMAIN_DRUPAL_IDENTIFIER_KVNL = 'kinderveiligheid_nl';

// Searchkit.
export let SEARCHKIT_HOST_URL = APP_OPTIONS.searchkitHostUrl;
export let SEARCHKIT_INDEX_ALL_CONTENT = APP_OPTIONS.searchkitIndexAllContent;
export let SEARCHKIT_INDEX_PROMOTED_CONTENT_THEMES =
  APP_OPTIONS.searchkitIndexPromotedContentThemes;
export const SEARCHKIT_PROMOTED_CONTENT_THEMES_HITS_PER_PAGE = 15;
export const SEARCHKIT_PROMOTED_CONTENT_THEMES_FACET_PILLAR = 'pillar';
export const SEARCHKIT_PROMOTED_CONTENT_THEMES_FACET_SUBJECT = 'subject_name';
export let SEARCHKIT_RESULT_ATTRIBUTES = APP_OPTIONS.searchkitResultAttributes;
export let SEARCHKIT_AUTOCOMPLETE_ATTRIBUTES =
  APP_OPTIONS.searchkitAutocompleteAttributes;
export let SEARCHKIT_AUTOCOMPLETE_FUZZINESS =
  APP_OPTIONS.searchkitAutocompleteFuzziness;
export let SEARCHKIT_AUTOCOMPLETE_HITS = APP_OPTIONS.searchkitAutocompleteHits;
export let SEARCHKIT_AUTOCOMPLETE_DEBOUNCE_TIME =
  APP_OPTIONS.searchkitAutocompleteDebounceTime;
export let SEARCHKIT_AUTOSUGGEST_ATTRIBUTES =
  APP_OPTIONS.searchkitAutosuggestAttributes;
export let SEARCHKIT_AUTOSUGGEST_HITS = APP_OPTIONS.searchkitAutosuggestHits;
export let SEARCHKIT_AUTOSUGGEST_DEBOUNCE_TIME =
  APP_OPTIONS.searchkitAutosuggestDebounceTime;
export let SEARCHKIT_AUTOCOMPLETE_RENDER_SUGGESTIONS_THRESHOLD =
  APP_OPTIONS.searchkitAutocompleteRenderSuggestionsThreshold;
export let SEARCH_PATH = APP_OPTIONS.searchPath;

// Taxonomies.
export const TAXONOMY_LOCATION = 'locatie';
export const TAXONOMY_SUBJECT = 'subject';
export const TAXONOMY_STAGEOFLIFE = 'stage_of_life';
export const TAXONOMY_CHAIN_APPROACH = 'chainapproach';
export const TAXONOMY_DOSSIER = 'dossier';
export const TAXONOMY_WORKFIELD = 'workfield';

// Paragraphs.
export const PARAGRAPH_THEME_CONTENT_OVERVIEW = 'theme_content_overview';

// Document variants.
export const TOOLKIT_CARD_VARIANT_EXTERNAL = 'external';
export const TOOLKIT_CARD_VARIANT_INTERNAL = 'internal';
export const TOOLKIT_CARD_VARIANT_DOCUMENT = 'document';

// Animaton.
export const PARALLAX_ANIMATION_SPEED = 8;
export const PARALLAX_ANIMATION_SPEED_FULLPAGE = 70;

// Leaflet.
export const MOVEMENTPROVIDER_COLORS = {
  blue: 'In Balans',
  green: 'Otago',
  orange: 'Vallen Verleden Tijd',
};

// TikTok Feed urls
export const KINDERVEILIGHEID_TIKTOK_URL = 'kinderveiligheid.nl';

// Applications
export const APPLICATON_VNL_ID = 'veiligheid';
export const SSO_APPLICATON_RISK_MONITOR_ID = 'risk_monitor';
export const SSO_APPLICATION_RISK_MONITOR_NAME = 'risico-monitor';

export const MAP_DEFAULT_BOUNDS: LatLngExpression[] = [
  [50.803721015, 3.31497114423],
  [53.5104033474, 7.09205325687],
];
export const MAP_DEFAULT_CENTER = {
  lat: 52.4011093,
  lng: 4.8986463,
};
export const MAP_DEFAULT_RADIUS = 10000; // 10km.
export const MAP_DEFAULT_ZOOM = 8;
export const MAP_MAX_ZOOM = 14;
export const MAP_MIN_ZOOM = 7;

// Experts link.
export const contactForm = '/experts/neem-contact-op';

// Spotler
export const SPOTLER_PERMISSIONS_BITS_WHITELIST = [
  1, 2, 4, 8, 16, 64, 128, 256,
];
export const SPOTLER_PERMISSIONS_HIDDEN_DEFAULT_BITS = [32];
export const SPOTLER_SOURCE_REGISTRATION = 'registration';

// Content types mapping.
export const CONTENT_TYPES = {
  ARTICLE: 'article',
  TOOL: 'tool',
  EXPERT: 'expert',
  EVENT: 'event',
  ELEARNING: 'e_learning',
  DIGITAL_TOOL: 'digital_tool',
  INTERVENTION: 'interventie',
  VACANCY: 'vacature',
  DOWNLOAD: 'download',
  RESEARCH: 'onderzoek',
  PRACTICAL_EXAMPLE: 'praktijkvoorbeeld',
  BASIC_PAGE: 'basic_page',
  OVERVIEW_PAGE: 'overview_page',
  MILESTONES: 'milestones',
  LANDINPAGE_PAGE: 'landing_page',
  CAMPAIGN: 'campaign',
  PARTNER: 'partner',
};

// Product variation types (as they appear in JSON:API product type strings)
export const PRODUCT_VARIATION_ELEARNING = 'e-learning';
export const PRODUCT_VARIATION_DIGITAL_TOOL = 'digital-tool';

export const ENTITY_TYPES = {
  NODE: 'node',
  TAXONOMY_TERM: 'taxonomy_term',
  WEBFORM: 'webform',
};

// Webform.
export const WEBFORM_CATEGORIES = {
  ASSESSMENT: 'Assessment',
  QUIZ: 'Quiz',
};

export const WEBFORM_TYPES = {
  WEBFORM_WIZARD_PAGE: 'webform_wizard_page',
};

// Error key set by IpSubmissionLimitWebformHandler (vnl_webform). Matches no
// element, so webform_rest returning it means the vote limit refused us.
export const WEBFORM_ERROR_IP_LIMIT = '_vote_limit';

export const URL_SSO_FLOW_IDENTIFIER = 'client_id';
export const OTHER_FORM_ITEM_ID = 'other';

// Use config to share static configuration between components and packages.
config.set(
  {
    SITE_NAME,
    MULTILINGUAL: false,
    DEFAULT_LANGUAGE,
    API_URL,
    REACT_APP_VERSION,
    // Set freeze to false to allow overwriting the configs based on the
    // options passed to window.reactFrontend
  },
  { freeze: false },
);

export const initConfig = (reactFrontendOptions: Partial<AppOptions> = {}) => {
  // Add passed reactFrontedOptions.parameters over APP_OPTIONS
  APP_OPTIONS = {
    ...APP_OPTIONS,
    ...reactFrontendOptions,
    ...reactFrontendOptions.parameters,
  };

  USE_SENTRY = APP_OPTIONS.sentryEnabled;
  SENTRY_DSN = APP_OPTIONS.sentryDSN;

  MULTILINGUAL = APP_OPTIONS.multilingual.toLowerCase() === 'true';
  DEFAULT_LANGUAGE = APP_OPTIONS.defaultLanguage;
  SITE_NAME = APP_OPTIONS.siteName;
  SEARCHKIT_INDEX_ALL_CONTENT = APP_OPTIONS.searchkitIndexAllContent;
  SEARCHKIT_INDEX_PROMOTED_CONTENT_THEMES =
    APP_OPTIONS.searchkitIndexPromotedContentThemes;
  SEARCHKIT_HOST_URL = APP_OPTIONS.searchkitHostUrl;
  THEME_MENU_FEATURED_BLOCK = APP_OPTIONS.themeMenuFeaturedBlock;
  SSO_SERVICES = APP_OPTIONS.ssoServices;
  MINIMUM_PASSWORD_CHARACTER_COUNT = APP_OPTIONS.minimumPasswordCharacterCount;

  SEARCHKIT_RESULT_ATTRIBUTES = APP_OPTIONS.searchkitResultAttributes;
  SEARCHKIT_AUTOCOMPLETE_ATTRIBUTES =
    APP_OPTIONS.searchkitAutocompleteAttributes;
  SEARCHKIT_AUTOCOMPLETE_FUZZINESS = APP_OPTIONS.searchkitAutocompleteFuzziness;
  SEARCHKIT_AUTOCOMPLETE_HITS = APP_OPTIONS.searchkitAutocompleteHits;
  SEARCHKIT_AUTOCOMPLETE_DEBOUNCE_TIME =
    APP_OPTIONS.searchkitAutocompleteDebounceTime;
  SEARCHKIT_AUTOSUGGEST_ATTRIBUTES = APP_OPTIONS.searchkitAutosuggestAttributes;
  SEARCHKIT_AUTOSUGGEST_HITS = APP_OPTIONS.searchkitAutosuggestHits;
  SEARCHKIT_AUTOSUGGEST_DEBOUNCE_TIME =
    APP_OPTIONS.searchkitAutosuggestDebounceTime;
  SEARCHKIT_AUTOCOMPLETE_RENDER_SUGGESTIONS_THRESHOLD =
    APP_OPTIONS.searchkitAutocompleteRenderSuggestionsThreshold;
  SEARCH_PATH = APP_OPTIONS.searchPath;

  // Use this to switch between domains for development
  API_URL = APP_OPTIONS.apiUrl;

  let CURRENT_DOMAIN = DOMAIN_VNL;
  let CURRENT_DOMAIN_DRUPAL_IDENTIFIER = DOMAIN_DRUPAL_IDENTIFIER_VNL;
  if (API_URL.match(/kinderveiligheid/) !== null) {
    CURRENT_DOMAIN = DOMAIN_KVNL;
    CURRENT_DOMAIN_DRUPAL_IDENTIFIER = DOMAIN_DRUPAL_IDENTIFIER_KVNL;
  }

  const RECAPTCHA_SITE_KEY = APP_OPTIONS.recapchaKey;
  const APOLLO_SEARCH_URL = APP_OPTIONS.apolloSearchUrl;
  const CONTENT_FETCH_CREDENTIALS = APP_OPTIONS.contentFetchCredentials;
  let CONTENT_FETCH_HEADERS = {};

  if (
    CONTENT_FETCH_CREDENTIALS !== 'include' &&
    API_URL.match(/local|devinc|staging|accept/)
  ) {
    CONTENT_FETCH_HEADERS = {
      'X-UncInc-SkipAuth': 'CorrectHorseBatteryStaple',
      ...CONTENT_FETCH_HEADERS,
    };
  }

  // Use config to share static configuration between components and packages.
  config.set({
    SITE_NAME,
    MULTILINGUAL,
    DEFAULT_LANGUAGE,
    API_URL,
    APOLLO_SEARCH_URL,
    CURRENT_DOMAIN,
    CURRENT_DOMAIN_DRUPAL_IDENTIFIER,
    RECAPTCHA_SITE_KEY,
    REACT_APP_VERSION,
    CONTENT_FETCH_CREDENTIALS,
    CONTENT_FETCH_HEADERS,
    THEME_MENU_FEATURED_BLOCK,
    MAPBOX_API_KEY: APP_OPTIONS.mapBoxApiKey,
    MAPBOX_STYLE: APP_OPTIONS.mapBoxStyle,
  });
};
